import { useEffect, useState } from 'react';
import * as smartbillSvc from '../../services/smartbill';
import { listProceduri } from '../../services/proceduri';
import { FilterPill } from './FilterPill';
import DateField from './DateField';
import { fmtDateRO } from '../../utils/dateFmt';
import '../../pages/Clienti/ClientiPage.css';

export const COMPANIE_OPTIONS = ['WAP', 'Cabinet', 'Inventa'];
export const MONEDA_OPTIONS   = ['RON', 'EUR', 'USD', 'CHF', 'GBP'];
export const LIMBA_OPTIONS    = [{ v: 'RO', l: 'Romana' }, { v: 'EN', l: 'Engleza' }];

export const TIP_PROC_LABEL = {
  marca_osim:             'Marca OSIM',
  marca_euipo:            'Marca EUIPO',
  marca_wipo:             'Marca WIPO / Madrid',
  marca_internationala:   'Marca Internationala',
  brevet:                 'Brevet / Model Utilitate',
  dmi:                    'Design / Model Industrial',
  idepot:                 'I-Depot Benelux',
  opozitie_osim:          'Opozitie OSIM',
  opozitie_euipo:         'Opozitie EUIPO',
  contestatie_osim:       'Contestatie OSIM',
  cai_atac_euipo:         'Cai de Atac EUIPO',
  notificare_aviz:        'Aviz Refuz / Notificare',
  dosar_instanta:         'Dosar Instanta',
  notificari_marketplace: 'Notificare Marketplace',
  opozitii:               'Opozitie',
  contestatii:            'Contestatie',
  dosare_instanta:        'Dosar Instanta (vechi)',
};

// Per-line service type — stored locally only (never sent to SmartBill), used
// for object-level reporting on the Financiar page.
export const OBJ_TIP_SERVICIU_OPTIONS = [
  { v: 'taxe',     l: 'Taxe' },
  { v: 'onorariu', l: 'Onorariu' },
];

export const emptyProdus = (tipServiciu = 'taxe') => ({
  _id: Math.random().toString(36).slice(2),
  name: '',
  tip_serviciu: tipServiciu,
  productDescription: '',
  code: '',
  measuringUnitName: 'buc',
  currency: 'RON',
  quantity: 1,
  price: '',
  isTaxIncluded: true,
  taxPercentage: 19,
  isService: true,
});

export const emptyMeta = (today) => ({
  tip_serviciu: 'taxe',
  companie: 'WAP',
  serie: '',
  data_emitere: today,
  data_scadenta: '',
  data_livrare: '',
  moneda: 'RON',
  curs_valutar: '',
  este_ciorna: false,
  trimis_email: false,
  email_cc: '',
  email_bcc: '',
  genereaza_link_plata: false,
  limba: 'RO',
  observatii: '',
  procedura_id: '',
});

/* ── FacturaModal ─────────────────────────────────────────── */
export default function FacturaModal({ client, proceduri: proceduriProp, tip: initialTip, convertDoc, editDoc, onClose, onSaved, showToast }) {
  const today = new Date().toISOString().slice(0, 10);
  const [proceduri, setProceduri] = useState(proceduriProp || []);
  useEffect(() => {
    if (proceduriProp !== undefined) { setProceduri(proceduriProp); return; }
    if (!client?.id) return;
    listProceduri(client.id).then(d => setProceduri(d.proceduri || [])).catch(() => {});
  }, [client?.id, proceduriProp]); // eslint-disable-line

  const metaFromDoc = (doc, keepSerie = false) => ({
    tip_serviciu:         doc.tip_serviciu || 'taxe',
    companie:             doc.companie || 'WAP',
    serie:                keepSerie ? (doc.serie || '') : '',
    data_emitere:         today,
    data_scadenta:        doc.data_scadenta || '',
    data_livrare:         doc.data_livrare  || '',
    moneda:               doc.moneda || 'RON',
    curs_valutar:         doc.curs_valutar ? String(doc.curs_valutar) : '',
    este_ciorna:          false,
    trimis_email:         doc.trimis_email  || false,
    email_cc:             doc.email_cc      || '',
    email_bcc:            doc.email_bcc     || '',
    genereaza_link_plata: doc.genereaza_link_plata || false,
    limba:                doc.limba  || 'RO',
    observatii:           doc.observatii || '',
    procedura_id:         doc.procedura_id ? String(doc.procedura_id) : '',
  });

  const produseDinDoc = (ps) => (ps || []).map(p => ({
    _id:                Math.random().toString(36).slice(2),
    name:               p.name               || '',
    tip_serviciu:       p.tip_serviciu        || 'taxe',
    productDescription: p.productDescription || '',
    code:               p.code               || '',
    measuringUnitName:  p.measuringUnitName  || 'buc',
    currency:           p.currency           || 'RON',
    quantity:           p.quantity           ?? 1,
    price:              p.price              ?? '',
    isTaxIncluded:      p.isTaxIncluded      ?? true,
    taxName:            p.taxName            || '',
    taxPercentage:      p.taxPercentage      ?? 19,
    isService:          p.isService          ?? true,
  }));

  const [meta, setMeta] = useState(
    convertDoc ? metaFromDoc(convertDoc) :
    editDoc    ? metaFromDoc(editDoc, true) :
    { ...emptyMeta(today), tip_serviciu: 'taxe' }
  );
  const [tip, setTip] = useState(
    convertDoc ? 'fiscala' : (editDoc ? editDoc.tip : (initialTip || 'proforma'))
  );
  const [produse, setProduse] = useState(
    convertDoc ? produseDinDoc(convertDoc.produse) :
    editDoc    ? produseDinDoc(editDoc.produse) :
    [emptyProdus()]
  );
  const [series,        setSeries]        = useState([]);
  const [taxRates,      setTaxRates]      = useState([]);
  const [saving,        setSaving]        = useState(false);
  const [fetchingRate,  setFetchingRate]  = useState(false);
  const [rateDate,      setRateDate]      = useState('');
  // Maps currency code → RON rate (1 EUR = 4.97 RON etc.)
  const [productRates,  setProductRates]  = useState({ RON: 1 });

  const procLabel = (p) => {
    const t = TIP_PROC_LABEL[p.tip_proc] || p.tip_proc;
    const nr = p.data?.numar || p.data?.numarDepozit || p.data?.denumire || '';
    return nr ? `${t} · ${nr}` : t;
  };

  useEffect(() => {
    smartbillSvc.getSeries(meta.companie, tip === 'proforma' ? 'p' : 'f')
      .then(r => setSeries(r.data?.list || []))
      .catch(() => setSeries([]));
  }, [meta.companie, tip]);

  useEffect(() => {
    smartbillSvc.getTaxRates(meta.companie)
      .then(r => {
        const raw = r.data;
        const all = raw?.taxes || raw?.list || (Array.isArray(raw) ? raw : []);
        const seen = new Set();
        const rates = all.filter(t => {
          const key = `${t.name}__${t.percentage}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
        if (rates.length > 0) {
          setTaxRates(rates);
          const defaultRate = rates.find(t => !t.name.toLowerCase().includes('veche')) || rates[0];
          setProduse(ps => ps.map(p =>
            p.taxName ? p : { ...p, taxName: defaultRate.name, taxPercentage: Number(defaultRate.percentage) }
          ));
        }
      })
      .catch(() => {});
  }, [meta.companie]);

  const setM = (patch) => setMeta(m => ({ ...m, ...patch }));

  // Keep productRates in sync with the invoice-level curs_valutar
  useEffect(() => {
    if (meta.moneda && meta.moneda !== 'RON') {
      const r = parseFloat(meta.curs_valutar);
      if (r > 0) setProductRates(prev => ({ ...prev, [meta.moneda]: r }));
    }
  }, [meta.moneda, meta.curs_valutar]);

  // Fetch and cache BNR rate for a product-level currency
  const fetchProductRate = async (currency) => {
    if (!currency || currency === 'RON') return;
    try {
      const r = await smartbillSvc.getExchangeRate(currency);
      const rate = parseFloat(r.data.rate);
      if (rate > 0) setProductRates(prev => ({ ...prev, [currency]: rate }));
    } catch {
      // ignore — totals will fall back to treating it as 1:1
    }
  };

  // Pre-fetch rates for any non-RON currencies already in the product list
  useEffect(() => {
    const currencies = [...new Set(produse.map(p => p.currency).filter(c => c && c !== 'RON'))];
    currencies.forEach(fetchProductRate);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleMonedaChange = async (moneda) => {
    setM({ moneda, curs_valutar: '' });
    setRateDate('');
    if (moneda === 'RON') return;
    setFetchingRate(true);
    try {
      const r = await smartbillSvc.getExchangeRate(moneda);
      setM({ curs_valutar: String(r.data.rate) });
      setRateDate(r.data.date || '');
    } catch {
      // leave empty — user can fill manually
    } finally {
      setFetchingRate(false);
    }
  };

  const addProdus    = () => setProduse(ps => [...ps, emptyProdus(meta.tip_serviciu === 'onorariu' ? 'onorariu' : 'taxe')]);
  const removeProdus = (id) => setProduse(ps => ps.filter(p => p._id !== id));
  const setProdus    = (id, patch) => {
    setProduse(ps => ps.map(p => p._id === id ? { ...p, ...patch } : p));
    if (patch.currency) fetchProductRate(patch.currency);
  };

  // Convert a price from itemCurrency to meta.moneda using cached BNR rates.
  // All rates are stored as "how many RON per 1 unit of that currency".
  const toCurrency = (price, itemCurrency) => {
    const mCur = meta.moneda || 'RON';
    if (!itemCurrency || itemCurrency === mCur) return price;
    const rateItem = productRates[itemCurrency] || 1;
    const rateMeta = mCur === 'RON' ? 1 : (productRates[mCur] || parseFloat(meta.curs_valutar) || 1);
    return price * rateItem / rateMeta;
  };

  const totalNet = produse.reduce((s, p) => {
    const qty = parseFloat(p.quantity) || 0;
    const price = parseFloat(p.price) || 0;
    const tva = p.taxPercentage / 100;
    const net = p.isTaxIncluded ? price / (1 + tva) : price;
    return s + toCurrency(net * qty, p.currency || 'RON');
  }, 0);
  const totalTva = produse.reduce((s, p) => {
    const qty = parseFloat(p.quantity) || 0;
    const price = parseFloat(p.price) || 0;
    const tva = p.taxPercentage / 100;
    const net = p.isTaxIncluded ? price / (1 + tva) : price;
    return s + toCurrency(net * qty * tva, p.currency || 'RON');
  }, 0);

  // True if any product uses a currency different from the invoice moneda
  const hasMixedCurrencies = produse.some(p => (p.currency || 'RON') !== (meta.moneda || 'RON'));

  const handleSave = async () => {
    if (!meta.serie)      { showToast('Selecteaza seria documentului'); return; }
    if (!meta.data_emitere) { showToast('Completeaza data emiterii'); return; }
    if (produse.some(p => !p.name || !p.price)) { showToast('Completeaza toate produsele/serviciile'); return; }
    setSaving(true);
    try {
      const mappedProduse = produse.map(({ _id, ...rest }) => ({
        ...rest,
        price:    toCurrency(parseFloat(rest.price) || 0, rest.currency || 'RON'),
        currency: meta.moneda || 'RON',
      }));

      const payload = {
        tip,
        tip_serviciu:  meta.tip_serviciu,
        companie:      meta.companie,
        procedura_id:  meta.procedura_id || null,
        ...(convertDoc ? { proforma_ref_serie: convertDoc.serie, proforma_ref_numar: convertDoc.numar } : {}),
        produse: mappedProduse,
        meta: {
          serie:               meta.serie,
          data_emitere:        meta.data_emitere,
          data_scadenta:       meta.data_scadenta || null,
          data_livrare:        meta.data_livrare  || null,
          moneda:              meta.moneda,
          curs_valutar:        meta.curs_valutar  || null,
          este_ciorna:         meta.este_ciorna,
          trimis_email:        meta.trimis_email,
          email_cc:            meta.email_cc,
          email_bcc:           meta.email_bcc,
          genereaza_link_plata: meta.genereaza_link_plata,
          limba:               meta.limba,
          observatii:          meta.observatii,
        },
      };
      const res = await smartbillSvc.createDocument(client.id, payload);
      showToast(`✓ ${tip === 'proforma' ? 'Proforma' : 'Factura fiscala'} emisa!`);
      onSaved(res.data.document);
      onClose();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Eroare la emitere document');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-box fm-modal">
        <div className="modal-sticky-header fm-header">
          <div className="fm-title-row">
            <div>
              <span className="fm-title">
                {convertDoc ? 'Converteste in factura fiscala' : editDoc ? 'Editeaza document' : 'Factura noua'}
              </span>
              {convertDoc && <span className="fm-subtitle"> — din proforma {convertDoc.serie}-{convertDoc.numar}</span>}
              {editDoc    && <span className="fm-subtitle"> — {editDoc.serie}-{editDoc.numar}</span>}
            </div>
            <button className="icon-circle-btn" onClick={onClose} title="Inchide">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
        </div>

        <div className="modal-scroll-body">
          <div className="f-row f-row-3">
            <div className="field">
              <label>Tip serviciu</label>
              <FilterPill value={meta.tip_serviciu} onChange={e => setM({ tip_serviciu: e.target.value })}>
                <option value="">— Tip serviciu —</option>
                <option value="taxe">Taxe</option>
                <option value="onorariu">Onorariu</option>
                <option value="mixt">Taxe + Onorariu</option>
              </FilterPill>
            </div>
            <div className="field">
              <label>Tip document</label>
              <FilterPill value={tip} onChange={e => setTip(e.target.value)} disabled={!!convertDoc || !!editDoc}>
                <option value="">— Tip document —</option>
                <option value="proforma">Proforma</option>
                <option value="fiscala">Factura fiscala</option>
              </FilterPill>
            </div>
            <div className="field">
              <label>Companie emitenta</label>
              <FilterPill value={meta.companie} onChange={e => setM({ companie: e.target.value, serie: '' })}>
                <option value="">— Companie —</option>
                {COMPANIE_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
              </FilterPill>
            </div>
          </div>

          <div className="f-row">
            <div className="field">
              <label>Serie</label>
              <FilterPill value={meta.serie} onChange={e => setM({ serie: e.target.value })}>
                <option value="">— Selecteaza seria —</option>
                {series.map(s => <option key={s.name} value={s.name}>{s.name}{s.nextNumber ? ` — nr. ${s.nextNumber}` : ''}</option>)}
              </FilterPill>
            </div>
            <div className="field">
              <label>Procedura asociata</label>
              <FilterPill searchable value={meta.procedura_id} onChange={e => setM({ procedura_id: e.target.value })}>
                <option value="">— Fara procedura —</option>
                {(proceduri || []).map(p => <option key={p.id} value={p.id}>{procLabel(p)}</option>)}
              </FilterPill>
            </div>
          </div>

          <div className="f-row f-row-3">
            <div className="field">
              <label>Data emitere</label>
              <DateField value={meta.data_emitere} onChange={v => setM({ data_emitere: v })} />
            </div>
            <div className="field">
              <label>Data scadenta</label>
              <DateField value={meta.data_scadenta} onChange={v => setM({ data_scadenta: v })} />
            </div>
            <div className="field">
              <label>Data livrare</label>
              <DateField value={meta.data_livrare} onChange={v => setM({ data_livrare: v })} />
            </div>
          </div>

          <div className="f-row f-row-3">
            <div className="field">
              <label>Moneda</label>
              <FilterPill value={meta.moneda} onChange={e => handleMonedaChange(e.target.value)}>
                <option value="">— Moneda —</option>
                {MONEDA_OPTIONS.map(m => <option key={m} value={m}>{m}</option>)}
              </FilterPill>
            </div>
            <div className="field">
              <label>
                Curs BNR (RON){rateDate ? <span className="fin-rate-date"> — {fmtDateRO(rateDate)}</span> : ''}
              </label>
              <input type="number" min="0" step="0.0001"
                placeholder={fetchingRate ? 'Se preia...' : meta.moneda === 'RON' ? '—' : 'ex: 5.0500'}
                value={meta.curs_valutar}
                onChange={e => setM({ curs_valutar: e.target.value })}
                disabled={fetchingRate || meta.moneda === 'RON'} />
            </div>
            <div className="field">
              <label>Limba</label>
              <FilterPill value={meta.limba} onChange={e => setM({ limba: e.target.value })}>
                <option value="">— Limba —</option>
                {LIMBA_OPTIONS.map(l => <option key={l.v} value={l.v}>{l.l}</option>)}
              </FilterPill>
            </div>
          </div>

          <div className="fm-checks">
            <label className="fin-check">
              <input type="checkbox" checked={meta.este_ciorna} onChange={e => setM({ este_ciorna: e.target.checked })} />
              <span>Ciorna</span>
            </label>
          </div>

          <div className="section-label" style={{ marginTop: 20 }}>Produse / Servicii</div>
          {produse.map((p, idx) => (
            <div key={p._id} className="fin-produs">
              <div className="fin-produs-head">
                <span className="fin-produs-nr">Pozitia {idx + 1}</span>
                {produse.length > 1 && (
                  <button type="button" className="fin-produs-del" onClick={() => removeProdus(p._id)}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                  </button>
                )}
              </div>
              <div className="f-row f-row-3 mb10">
                <div className="field">
                  <label>Denumire</label>
                  <input type="text" placeholder="Denumire produs/serviciu" value={p.name} onChange={e => setProdus(p._id, { name: e.target.value })} />
                </div>
                <div className="field">
                  <label>Tip serviciu</label>
                  <FilterPill value={p.tip_serviciu || 'taxe'} onChange={e => setProdus(p._id, { tip_serviciu: e.target.value })}>
                    {OBJ_TIP_SERVICIU_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                  </FilterPill>
                </div>
                <div className="field">
                  <label>Cod <span className="f-hint">(optional)</span></label>
                  <input type="text" placeholder="SKU / cod intern" value={p.code} onChange={e => setProdus(p._id, { code: e.target.value })} />
                </div>
              </div>
              <div className="field mb10">
                <label>Descriere <span className="f-hint">(optional)</span></label>
                <input type="text" placeholder="Text suplimentar afisat pe document" value={p.productDescription} onChange={e => setProdus(p._id, { productDescription: e.target.value })} />
              </div>
              <div className="f-row f-row-3">
                <div className="field">
                  <label>UM</label>
                  <input type="text" value={p.measuringUnitName} onChange={e => setProdus(p._id, { measuringUnitName: e.target.value })} />
                </div>
                <div className="field">
                  <label>Cantitate</label>
                  <input type="number" min="0" step="0.01" value={p.quantity} onChange={e => setProdus(p._id, { quantity: e.target.value })} />
                </div>
                <div className="field">
                  <label>Moneda</label>
                  <FilterPill value={p.currency || 'RON'} onChange={e => setProdus(p._id, { currency: e.target.value })}>
                    {MONEDA_OPTIONS.map(m => <option key={m} value={m}>{m}</option>)}
                  </FilterPill>
                </div>
              </div>
              <div className="f-row f-row-3">
                <div className="field">
                  <label>Pret ({p.currency || 'RON'})</label>
                  <input type="number" min="0" step="0.01" placeholder="0.00" value={p.price} onChange={e => setProdus(p._id, { price: e.target.value })} />
                </div>
                <div className="field">
                  <label>TVA</label>
                  <FilterPill
                    value={p.taxName || ''}
                    onChange={e => {
                      const rate = taxRates.find(r => r.name === e.target.value);
                      setProdus(p._id, { taxName: e.target.value, taxPercentage: rate ? Number(rate.percentage) : 19 });
                    }}
                  >
                    <option value="">{taxRates.length === 0 ? 'Se incarca...' : '— TVA —'}</option>
                    {taxRates.map(r => (
                      <option key={`${r.name}__${r.percentage}`} value={r.name}>
                        {Number(r.percentage)}% — {r.name}
                      </option>
                    ))}
                  </FilterPill>
                </div>
                <div className="field">
                  <label>Pret cu TVA</label>
                  <FilterPill value={String(p.isTaxIncluded)} onChange={e => setProdus(p._id, { isTaxIncluded: e.target.value === 'true' })}>
                    <option value="">— TVA inclus —</option>
                    <option value="true">Da</option>
                    <option value="false">Nu</option>
                  </FilterPill>
                </div>
              </div>
            </div>
          ))}

          <button type="button" className="btn btn-secondary btn-sm fm-add-btn" onClick={addProdus}>
            + Adauga pozitie
          </button>

          {(() => {
            const mCur  = meta.moneda || 'RON';
            const curs  = mCur === 'RON' ? 1 : (parseFloat(meta.curs_valutar) || 1);
            const inRon = mCur !== 'RON' && curs > 1;
            const netRon = totalNet * curs;
            const tvaRon = totalTva * curs;
            const totRon = (totalNet + totalTva) * curs;
            const convNote = hasMixedCurrencies ? ' (convertit la curs BNR)' : '';
            return (
              <div className="fin-totals">
                {hasMixedCurrencies && (
                  <div className="fin-total-row fin-rate-note">
                    <span>Cursuri aplicate: {
                      [...new Set(produse.map(p => p.currency || 'RON').filter(c => c !== mCur))]
                        .map(c => `1 ${c} = ${(productRates[c] || 1).toFixed(4)} RON`)
                        .join(', ')
                    }</span>
                  </div>
                )}
                <div className="fin-total-row"><span>Total fara TVA{convNote}</span><span>{totalNet.toFixed(2)} {mCur}{inRon ? ` = ${netRon.toFixed(2)} RON` : ''}</span></div>
                <div className="fin-total-row"><span>TVA{convNote}</span><span>{totalTva.toFixed(2)} {mCur}{inRon ? ` = ${tvaRon.toFixed(2)} RON` : ''}</span></div>
                <div className="fin-total-row fin-total-row--bold"><span>Total</span><span>{(totalNet + totalTva).toFixed(2)} {mCur}{inRon ? ` = ${totRon.toFixed(2)} RON` : ''}</span></div>
              </div>
            );
          })()}

          <div className="field" style={{ marginTop: 16 }}>
            <label>Observatii / text document</label>
            <textarea rows={3} placeholder="Text afisat pe document (optional)" value={meta.observatii} onChange={e => setM({ observatii: e.target.value })} />
          </div>
        </div>

        <div className="fm-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>Renunta</button>
          <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Se emite...' : `Emite ${tip === 'proforma' ? 'proforma' : 'factura fiscala'}`}
          </button>
        </div>
      </div>
    </div>
  );
}
